import React, {
  FocusEventHandler,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
  CSSProperties,
} from "react";

import {
  Tooltip,
  Typography,
  Checkbox,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import {
  colors,
  fontSize,
  fontWeight,
  lineHeight,
  padding,
} from "../../../theme/defaultStyle";
import { styled } from "@mui/material/styles";

export type OptionRenderType = {
  label: string;
  value: string;
};

export type StewartAutocompleteEventType = {
  target: { name: string; value: OptionRenderType[] };
};

type StewartAutocompleteProps<T> = {
  placeholder?: string;
  name: keyof T;
  options: any[];
  optionsTextField?: string;
  optionsValueField?: string;
  disabled?: boolean;
  clearSelectionOnDisable?: boolean;
  onChange?: (
    e: StewartAutocompleteEventType,
    value: OptionRenderType[]
  ) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  controlStyle?: CSSProperties;
  readOnly?: boolean;
  hasUntouchedValidation?: boolean;
  required?: boolean;
  freeSolo?: boolean;
  defaultSelectedOption?: OptionRenderType | OptionRenderType[];
  getOptionDisabled?: (option: OptionRenderType) => boolean;
  onOpen?: (event?: SyntheticEvent) => void;
  onClose?: (event?: SyntheticEvent) => void;
  textFieldRef?: React.RefObject<HTMLInputElement>;
  hasErrorIn?: boolean;
  inputClassName?: string;
  errorMessage?: string;
  disablePortal?: boolean;
  autoSelectSingleOption?: boolean;
  // Async / typeahead mode: parent supplies the user-typed text via this
  // callback (debounced upstream) and refreshes `options` accordingly. When
  // set, the single-result auto-commit is suppressed and MUI's built-in
  // client-side filtering is disabled (server already filtered).
  onInputChange?: (text: string) => void;
  loading?: boolean;
  // Render the selected tags using `.value` instead of `.label`. Useful when
  // labels are verbose but the underlying ID is short enough to display
  // inline (e.g., AgentID dropdown showing CompanyIDs).
  showValueAsTag?: boolean;
  // Hard cap on the rendered match count. Caps both the initial "show all"
  // view AND the live-filtered set (as user types). Substring filter
  // behavior is unchanged; only the result count is bounded.
  filterOptionsLimit?: number;
  // Opt back into MUI's native clear-all X button. Default is suppressed
  // (the multi-checkbox UX usually toggles items individually); column
  // headers want it on so users can wipe all selections in one click.
  enableClearable?: boolean;
  // Suppress MUI's floating label entirely and render the placeholder as
  // visible muted-grey text. Use when an external label already labels the
  // field and the in-input "float to top on focus" behavior is unwanted.
  hideLabel?: boolean;
  // Let the dropdown size to its options instead of inheriting the anchor's
  // width. For narrow columns (the State grid column is 100px) MUI's default
  // clips the option labels.
  popperMinWidth?: number;
};

const CustomTextField = styled(TextField)(() => ({
  "& .MuiInputBase-input.Mui-disabled": {
    backgroundColor: colors.white01,
  },
  "& input::placeholder": {
    color: `${colors.black01} !important`,
  },
}));

const StyledAutocomplete = styled(Autocomplete)(() => ({
  "& .MuiInputBase-root": { height: "40px", padding: `${padding.zero} ${padding.large1} !important` },
  "& .MuiOutlinedInput-root": {
    flexWrap: "nowrap !important",
    "&.Mui-focused fieldset": {
      border: `2px solid ${colors.blue01} !important`,
    },
    "&.Mui-disabled": {
      backgroundColor: colors.white01,
    },
    "& .MuiOutlinedInput-input": {
      padding: `${padding.zero} ${padding.xsmall2} !important`,
      height: "40px !important",
      margin: "0 !important",
    },
  },
  "& .MuiInputLabel-outlined": {
    "&.MuiInputLabel-shrink": {
      top: "2px !important",
    },
  },
  ".MuiSvgIcon-root": {
    fill: `${colors.blue01} !important`,
  },
}));

const StyledTypography = styled(Typography)(() => ({
  fontSize: `${fontSize.large} !important`,
  fontWeight: `${fontWeight.normal1} !important`,
   lineHeight: `${lineHeight.xlarge40} !important`,
}));

const StewartAutocompleteWithCheckboxes = <T,>({
  placeholder = "",
  name,
  optionsTextField = "label",
  optionsValueField = "value",
  options,
  onChange = () => {},
  onBlur = () => null,
  controlStyle = {},
  readOnly = false,
  disabled = false,
  clearSelectionOnDisable = false,
  hasUntouchedValidation = false,
  required = false,
  freeSolo = false,
  getOptionDisabled,
  onOpen,
  onClose,
  defaultSelectedOption,
  hasErrorIn,
  inputClassName = "",
  errorMessage = "",
  disablePortal = true,
  autoSelectSingleOption = true,
  onInputChange,
  loading = false,
  showValueAsTag = false,
  filterOptionsLimit,
  enableClearable = false,
  hideLabel = false,
  popperMinWidth,
}: StewartAutocompleteProps<T>) => {
  const isAsync = typeof onInputChange === "function";

  // Memoized substring filter capped to `filterOptionsLimit`. Not used in
  // async mode (server already filtered) — that branch stays `(x) => x`.
  // Typed as `any` because StyledAutocomplete keeps the unknown-option
  // signature; createFilterOptions's stricter generic clashes otherwise.
  const limitedFilter = useMemo(
    () =>
      typeof filterOptionsLimit === "number"
        ? createFilterOptions<any>({ limit: filterOptionsLimit })
        : undefined,
    [filterOptionsLimit]
  );
  // Async mode opens the popup only as a direct result of typing (see the
  // `open` prop below), so a blank field never re-shows the last search's
  // results. Empty string = nothing typed = stay shut.
  const [searchText, setSearchText] = useState("");
  const [fieldValue, setFieldValue] = useState<OptionRenderType[]>(() => {
    // Initialize with default value if provided
    if (defaultSelectedOption) {
      return Array.isArray(defaultSelectedOption) ? defaultSelectedOption : [defaultSelectedOption];
    }
    return [];
  });

  const optionsToRender: OptionRenderType[] = options
    .map((option) => ({
      label: option[optionsTextField],
      value: option[optionsValueField],
    }))
    .filter((option) => option.label);

  useEffect(() => {
    if (!autoSelectSingleOption) return;
    // In async mode, options change as the user types; auto-committing a
    // transient single match would steal selection from the user mid-type.
    if (isAsync) return;
    if (options.length === 1 && fieldValue.length === 0) {
      const singleOption = {
        label: options[0][optionsTextField],
        value: options[0][optionsValueField],
      };
      setFieldValue([singleOption]);
      onChange(
        { target: { name: name.toString(), value: [singleOption] } },
        [singleOption]
      );
    }
  }, [options, autoSelectSingleOption, isAsync]);

  useEffect(() => {
    if (clearSelectionOnDisable && disabled && fieldValue.length > 0) {
      setFieldValue([]);
      onChange({ target: { name: name.toString(), value: [] } }, []);
    }
  }, [disabled, clearSelectionOnDisable, onChange, name, fieldValue.length]);

  const hasError =
    (fieldValue.length === 0 && hasUntouchedValidation && !defaultSelectedOption) || Boolean(errorMessage);

  const handleOnChange = (
    event: SyntheticEvent,
    value: OptionRenderType[],
  ) => {
    setFieldValue(value);
    onChange(
      { target: { name: name.toString(), value } },
      value
    );
  };

  return (
    <StyledAutocomplete
      id={name.toString()}
      options={optionsToRender}
      value={fieldValue}
      disablePortal={disablePortal}
      // MUI pins the popper to `width: anchorEl.clientWidth`; slot props are
      // spread after that inline style, so this replaces it outright. Width is
      // the only thing that style sets, so nothing else is lost.
      {...(popperMinWidth
        ? {
            slotProps: {
              popper: {
                style: { width: "max-content", minWidth: popperMinWidth },
              },
            },
          }
        : {})}
      onChange={(event, value, reason, details) => handleOnChange(event, value as OptionRenderType[])}
      onInputChange={(_, val, reason) => {
        if (!isAsync) return;
        if (reason === "input") {
          setSearchText(val);
          onInputChange?.(val);
        } else if (reason === "clear") {
          // The X button fires no close event at all, so reset here —
          // otherwise a visibly empty field still counts as an active search.
          setSearchText("");
          onInputChange?.("");
        }
        // "reset" is MUI blanking the input after a selection; ignore it so
        // disableCloseOnSelect can keep the list up for further ticks.
      }}
      loading={loading}
      loadingText="Searching…"
      filterOptions={isAsync ? (x) => x : limitedFilter}
      onBlur={onBlur}
      // Typeahead mode: open only when the user has typed something AND the
      // server has returned matches for it. Focus and click alone never open
      // the list, so a blank field can't resurrect the previous search —
      // mirrors the File Name/Number field on the Portal's Create File page.
      // Static mode stays uncontrolled so those dropdowns still open on click.
      {...(isAsync
        ? {
            open: searchText.trim().length > 0 && optionsToRender.length > 0,
            onOpen,
            onClose: (e: SyntheticEvent) => {
              setSearchText("");
              onClose?.(e);
            },
          }
        : { onOpen, onClose })}
      getOptionDisabled={(option) => Boolean(getOptionDisabled?.(option as OptionRenderType))}
      isOptionEqualToValue={(option, value) =>
        (option as OptionRenderType).value === (value as OptionRenderType).value
      }
      getOptionLabel={(option) => (option as OptionRenderType).label}
      renderTags={(value) => {
        const items = value as OptionRenderType[];
        const text = items
          .map((e) => (showValueAsTag ? e.value : e.label))
          .join(", ");
        return (
          <Tooltip title={text} placement="right" arrow>
            <StyledTypography noWrap>{text}</StyledTypography>
          </Tooltip>
        );
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            sx={{ "& .MuiSvgIcon-root": { color: colors.blue01 } }}
            checked={selected}
          />
          <span style={{ whiteSpace: "nowrap" }}>
            {(option as OptionRenderType).label}
          </span>
        </li>
      )}
      renderInput={(params) => (
        <CustomTextField
          {...params}
          label={hideLabel ? "" : placeholder !== "Search" ? placeholder : ""}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            style: { padding: padding.zero },
          }}
          InputLabelProps={{
            shrink: Boolean(fieldValue.length) || Boolean(params.inputProps?.value),
          }}
          variant="outlined"
          required={required}
          error={hasError || hasErrorIn}
          sx={
            hideLabel
              ? {
                  "& input::placeholder": {
                    color: `${colors.grey06} !important`,
                    opacity: 1,
                  },
                }
              : undefined
          }
        />
      )}
      multiple
      disableCloseOnSelect
      {...(enableClearable ? {} : { disableClearable: true })}
      readOnly={readOnly}
      disabled={disabled}
      freeSolo={freeSolo}
      style={{ display: "flex", flexDirection: "column", width: "100%", ...controlStyle }}
      className={inputClassName}
    />
  );
};

export default StewartAutocompleteWithCheckboxes;
